/**
 * Revision Detail Sheet
 * Sheet/modal component showing revision details with "Updates Made" and "Revision Preview" tabs
 */

import React, { useState } from 'react';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    History,
    RotateCcw,
    Check,
    X,
    Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
    type Revision,
    type RevisionActivity,
    getRevisionUserName,
    formatRevisionTime,
    getActionLabel,
} from '@/hooks/useRevisions';
import { useFields } from '@/hooks/useFields';
import { getFieldDisplayName } from '@/utils/get-field-display-name';

interface RevisionDetailSheetProps {
    revision: Revision | null;
    previousRevision: Revision | null;  // The revision before this one in the timeline
    collection?: string;  // Collection name to fetch fields for display names
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onRevert?: (data: Record<string, any>) => void;
}

export function RevisionDetailSheet({
    revision,
    previousRevision,
    collection,
    open,
    onOpenChange,
    onRevert,
}: RevisionDetailSheetProps) {
    const [activeTab, setActiveTab] = useState<string>('updates');
    const [revertDialogOpen, setRevertDialogOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
    
    // Fetch fields for display names
    const { fields } = useFields({ collection: collection || null, includeHidden: true });
    const fieldMap = new Map(fields.map(f => [f.field, f]));

    // Initialize selected fields with fields that have differences
    React.useEffect(() => {
        if (revision?.delta && revision.data) {
            const beforeData = previousRevision?.data || {};
            const afterData = revision.data;

            // Only select fields that actually have differences
            const changedFields = Object.keys(revision.delta).filter(field => {
                const oldValue = beforeData[field];
                const newValue = afterData[field];
                return JSON.stringify(oldValue) !== JSON.stringify(newValue);
            });
            setSelectedFields(new Set(changedFields));
        }
    }, [revision, previousRevision]);

    if (!revision) return null;

    const activity = revision.activity as RevisionActivity;
    const isActivityExpanded = typeof activity !== 'number';

    const userName = isActivityExpanded ? getRevisionUserName(activity) : 'Unknown';
    const timeInfo = isActivityExpanded
        ? formatRevisionTime(activity.timestamp)
        : { relative: 'Unknown', absolute: '' };
    const actionLabel = isActivityExpanded ? getActionLabel(activity.action) : 'Unknown';

    // Get user info
    const user = isActivityExpanded && typeof activity.user === 'object' ? activity.user : null;
    const userAvatar = user?.avatar || null;
    const userInitials = userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    const handleRevert = () => {
        if (!revision?.data || !onRevert) return;

        // Build the revert data from selected fields
        // We revert to the values from this revision (revision.data contains the state at this revision)
        const revertData: Record<string, any> = {};
        const appliedFields: string[] = [];
        
        for (const field of selectedFields) {
            if (field in revision.data) {
                // Get the value from the revision data (this is the state we want to restore)
                revertData[field] = revision.data[field];
                const fieldObj = fieldMap.get(field);
                const displayName = fieldObj ? getFieldDisplayName(fieldObj) : field;
                appliedFields.push(displayName);
            }
        }

        // Apply changes to the form (not saved to DB)
        // Pass the revert data which will be merged into the form state
        onRevert(revertData);
        
        // Show toast with field names
        if (appliedFields.length > 0) {
            const fieldList = appliedFields.length <= 3 
                ? appliedFields.join(', ')
                : `${appliedFields.slice(0, 3).join(', ')} and ${appliedFields.length - 3} more`;
            toast.success(`Applying changes to ${fieldList}. Remember to save!`);
        } else {
            toast.success('Changes applied to form. Remember to save!');
        }
        
        setRevertDialogOpen(false);
        onOpenChange(false);
    };

    const toggleFieldSelection = (field: string) => {
        setSelectedFields(prev => {
            const next = new Set(prev);
            if (next.has(field)) {
                next.delete(field);
            } else {
                next.add(field);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (!revision?.delta || !revision.data) return;

        const beforeData = previousRevision?.data || {};
        const afterData = revision.data;

        // Get all fields that have differences
        const changedFields = Object.keys(revision.delta).filter(field => {
            const oldValue = beforeData[field];
            const newValue = afterData[field];
            return JSON.stringify(oldValue) !== JSON.stringify(newValue);
        });

        if (selectedFields.size === changedFields.length) {
            setSelectedFields(new Set());
        } else {
            setSelectedFields(new Set(changedFields));
        }
    };

    // Check if we can revert (need data snapshot to revert to)
    const canRevert = !!revision.data && Object.keys(revision.data).length > 0 && activity.action !== 'delete';

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className="w-[500px] sm:w-[600px] sm:max-w-none flex flex-col p-0 h-full max-h-screen">
                    <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                    {userAvatar && <AvatarImage src={userAvatar} />}
                                    <AvatarFallback>{userInitials}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <SheetTitle className="text-left">{userName}</SheetTitle>
                                    <SheetDescription className="text-left">
                                        <span className={cn(
                                            "inline-flex items-center mr-2 px-1.5 py-0.5 rounded text-xs",
                                            activity.action === 'create' && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                                            activity.action === 'update' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                                            activity.action === 'delete' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                                            activity.action === 'revert' && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                                        )}>
                                            {actionLabel}
                                        </span>
                                        <span title={timeInfo.absolute}>{timeInfo.relative}</span>
                                    </SheetDescription>
                                </div>
                            </div>
                            {canRevert && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setRevertDialogOpen(true)}
                                >
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                    Revert
                                </Button>
                            )}
                        </div>
                    </SheetHeader>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                        <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6 flex-shrink-0">
                            <TabsTrigger
                                value="updates"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
                            >
                                Updates Made
                            </TabsTrigger>
                            <TabsTrigger
                                value="preview"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
                            >
                                Revision Preview
                            </TabsTrigger>
                        </TabsList>

                        <div className="flex-1 min-h-0 overflow-hidden">
                            <ScrollArea className="h-full">
                                <TabsContent value="updates" className="mt-0 p-6 space-y-4">
                                    {/* Info Banner */}
                                    <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                                        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-blue-900 dark:text-blue-100">
                                            <p className="font-medium mb-1">Below are the specific changes made in this revision.</p>
                                            <p className="text-blue-700 dark:text-blue-300">Keep in mind that relational data is not included here.</p>
                                        </div>
                                    </div>

                                    <UpdatesMade
                                        delta={revision.delta}
                                        beforeData={previousRevision?.data || {}}
                                        afterData={revision.data}
                                        selectedFields={selectedFields}
                                        onToggleField={toggleFieldSelection}
                                        onToggleAll={toggleSelectAll}
                                        fieldMap={fieldMap}
                                    />
                                </TabsContent>

                                <TabsContent value="preview" className="mt-0 p-6">
                                    <RevisionPreview data={revision.data} fieldMap={fieldMap} />
                                </TabsContent>
                            </ScrollArea>
                        </div>
                    </Tabs>
                </SheetContent>
            </Sheet>

            {/* Revert Confirmation Dialog */}
            <AlertDialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Apply revision changes to form?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {(() => {
                                const fieldNames = Array.from(selectedFields)
                                    .map(field => {
                                        const fieldObj = fieldMap.get(field);
                                        return fieldObj ? getFieldDisplayName(fieldObj) : field;
                                    })
                                    .slice(0, 5);
                                
                                const remaining = selectedFields.size - fieldNames.length;
                                const fieldList = fieldNames.length <= 3
                                    ? fieldNames.join(', ')
                                    : `${fieldNames.slice(0, 3).join(', ')}${remaining > 0 ? ` and ${remaining} more` : ''}`;
                                
                                return `This will apply changes to ${fieldList} from this revision to your current form. Remember to save the changes after applying them.`;
                            })()}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRevert} disabled={selectedFields.size === 0}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Apply Changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

// Component showing the changes (delta) with before/after values and checkboxes
function UpdatesMade({
    delta,
    beforeData,
    afterData,
    selectedFields,
    onToggleField,
    onToggleAll,
    fieldMap
}: {
    delta: Record<string, any> | null;
    beforeData: Record<string, any>;
    afterData: Record<string, any> | null;
    selectedFields: Set<string>;
    onToggleField: (field: string) => void;
    onToggleAll: () => void;
    fieldMap: Map<string, any>;
}) {
    if (!delta || Object.keys(delta).length === 0) {
        return (
            <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                    No changes recorded for this revision
                </p>
            </div>
        );
    }

    if (!afterData) {
        return (
            <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                    No data snapshot available for this revision
                </p>
            </div>
        );
    }

    // Get fields that actually have differences
    const changedFields = Object.keys(delta).filter(field => {
        const oldValue = beforeData[field];
        const newValue = afterData[field];
        return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    });

    if (changedFields.length === 0) {
        return (
            <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                    No differences found
                </p>
            </div>
        );
    }

    const allSelected = selectedFields.size === changedFields.length;

    return (
        <div className="space-y-4">
            {/* Select All */}
            <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox
                    checked={allSelected}
                    onCheckedChange={onToggleAll}
                    id="select-all"
                />
                <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                    Select All Fields ({changedFields.length} change{changedFields.length !== 1 ? 's' : ''})
                </label>
            </div>

            {changedFields.map((field) => {
                const oldValue = beforeData[field];
                const newValue = afterData[field];
                const isSelected = selectedFields.has(field);
                const fieldObj = fieldMap.get(field);
                const displayName = fieldObj ? getFieldDisplayName(fieldObj) : field;

                return (
                    <div key={field} className={cn(
                        "border rounded-lg p-4 transition-colors",
                        isSelected ? "border-primary bg-primary/5" : ""
                    )}>
                        <div className="flex items-center gap-3 mb-3">
                            <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => onToggleField(field)}
                                id={`field-${field}`}
                            />
                            <label htmlFor={`field-${field}`} className="flex items-center gap-2 cursor-pointer flex-1">
                                <span className="font-medium text-sm">
                                    {displayName}
                                </span>
                                {displayName !== field && (
                                    <Badge variant="outline" className="font-mono text-xs">
                                        {field}
                                    </Badge>
                                )}
                            </label>
                        </div>
                        <div className="ml-7 space-y-2">
                            <div className="flex items-start gap-2">
                                <span className="text-xs text-red-600 dark:text-red-400 w-16 pt-2 flex items-center gap-1">
                                    <X className="h-3 w-3" />
                                    Before:
                                </span>
                                <div className="flex-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
                                    <FieldValue value={oldValue} />
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-xs text-green-600 dark:text-green-400 w-16 pt-2 flex items-center gap-1">
                                    <Check className="h-3 w-3" />
                                    After:
                                </span>
                                <div className="flex-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-2">
                                    <FieldValue value={newValue} />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// Component showing the full data preview
function RevisionPreview({ data, fieldMap }: { data: Record<string, any> | null; fieldMap: Map<string, any> }) {
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                    No data snapshot available for this revision
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {Object.entries(data).map(([field, value]) => {
                const fieldObj = fieldMap.get(field);
                const displayName = fieldObj ? getFieldDisplayName(fieldObj) : field;
                
                return (
                    <div key={field} className="flex">
                        <div className="w-1/3 text-sm text-muted-foreground truncate pr-4">
                            <div className="font-medium">{displayName}</div>
                            {displayName !== field && (
                                <div className="text-xs font-mono text-muted-foreground/70 mt-0.5">
                                    {field}
                                </div>
                            )}
                        </div>
                        <div className="w-2/3 text-sm">
                            <FieldValue value={value} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// Helper component to render field values
function FieldValue({ value }: { value: any }) {
    if (value === null || value === undefined) {
        return <span className="text-muted-foreground italic">null</span>;
    }

    if (typeof value === 'boolean') {
        return value ? (
            <Check className="h-4 w-4 text-green-500" />
        ) : (
            <X className="h-4 w-4 text-red-500" />
        );
    }

    if (typeof value === 'object') {
        return (
            <pre className="text-xs bg-muted/50 p-2 rounded overflow-auto max-h-40">
                {JSON.stringify(value, null, 2)}
            </pre>
        );
    }

    // Check if it's a date string
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return <span>{date.toLocaleString()}</span>;
            }
        } catch {
            // Not a valid date, render as string
        }
    }

    return <span className="break-all">{String(value)}</span>;
}
